import { useI18n } from '../../../i18n';
import { ReactNode } from 'react';
import { AlertTriangleIcon } from '../../../components/icons';
import { cn } from '../../../utils/cn';
import { useMoney } from '../../../providers/CurrencyProvider';

export interface ProductStats {
  totalProducts: number;
  activeProducts: number;
  inactiveProducts: number;
  averagePrice: number;
  withBarcodeRate: number;
  lowStockAlerts?: number;
  totalValue?: number;
}

interface ProductStatsRowProps {
  stats: ProductStats;
  isLoading?: boolean;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function TrendUpIcon() {
  const { t } = useI18n();
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M9.5 3.5H13.5V7.5" stroke="#16A34A" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.5 3.5L7.85 9.15C7.75654 9.24161 7.63088 9.29293 7.5 9.29293C7.36912 9.29293 7.24346 9.24161 7.15 9.15L4.85 6.85C4.75654 6.75839 4.63088 6.70707 4.5 6.70707C4.36912 6.70707 4.24346 6.75839 4.15 6.85L0.5 10.5" stroke="#16A34A" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TrendDownIcon() {
  const { t } = useI18n();
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M9.5 10.5H13.5V6.5" stroke="#BA1A1A" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.5 10.5L7.85 4.85C7.75654 4.75839 7.63088 4.70707 7.5 4.70707C7.36912 4.70707 7.24346 4.75839 7.15 4.85L4.85 7.15C4.75654 7.24161 4.63088 7.29293 4.5 7.29293C4.36912 7.29293 4.24346 7.24161 4.15 7.15L0.5 3.5" stroke="#BA1A1A" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  icon: ReactNode;
  iconBg: string;
  trend?: string;
  trendUp?: boolean;
}

function StatCard({ label, value, icon, iconBg, trend, trendUp }: StatCardProps) {
  const { t } = useI18n();
  const parts = trend ? trend.split(' ') : [];
  const trendNumber = parts[0] ?? '';
  const trendSuffix = parts.slice(1).join(' ');

  return (
    <div className="flex items-start justify-between gap-2 rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-slate-500">{label}</p>
        <p className="mt-1 break-words text-xl font-bold leading-8 text-[#014361] sm:text-[32px] sm:leading-[40px]">{value}</p>
        {trend && (
          <p className="mt-1.5 flex items-center gap-1 text-xs font-medium">
            <span className={cn('flex items-center gap-0.5 font-bold', trendUp ? 'text-emerald-500' : 'text-red-500')}>
              {trendUp ? <TrendUpIcon /> : <TrendDownIcon />}
              {trendNumber}
            </span>
            <span className="text-slate-800">{trendSuffix}</span>
          </p>
        )}
      </div>
      <div className={cn('mt-1 flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl sm:h-11 sm:w-11 [&_svg]:h-6 [&_svg]:w-6 sm:[&_svg]:h-8 sm:[&_svg]:w-8', iconBg)}>
        {icon}
      </div>
    </div>
  );
}

export function ProductStatsRow({ stats, isLoading }: ProductStatsRowProps) {
  const { t } = useI18n();
  const money = useMoney();

  // Helper nằm trong component vì `useMoney()` là hook.
  const formatCurrency = (value: number): string =>
    money.format(value, { minimumFractionDigits: 0 });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-[110px] animate-pulse rounded-xl bg-white shadow-sm border border-slate-100" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label={t('totalProduct')}
        value={formatNumber(stats.totalProducts)}
        iconBg="bg-blue-50"
        icon={
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M10.667 9.335V8.001a5.333 5.333 0 0110.666 0v1.334h4a1.333 1.333 0 011.334 1.343v16.001a2.667 2.667 0 01-2.659 2.654H7.992a2.667 2.667 0 01-2.659-2.654V10.679a1.333 1.333 0 011.334-1.344h4zm1.6 0h7.466V8.001a2.667 2.667 0 00-5.333 0v1.334zm-1.6 1.6H6.934v15.74a.667.667 0 00.659.666h16.016a.667.667 0 00.659-.666V10.935h-3.734v3.733h-1.6v-3.733h-7.467v3.733h-1.6V10.935z" fill="#0288D1" />
          </svg>
        }
      />
      <StatCard
        label={t('activeSkus')}
        value={formatNumber(stats.activeProducts)}
        iconBg="bg-emerald-50"
        icon={
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M16 4.663l10.013 3.687L16 12.037 5.987 8.35 16 4.663zM4.05 9.322v12.792l11.9 4.685V13.631L4.05 9.322zm23.9 0L16 13.631v13.168l11.95-4.685V9.322z" stroke="#219653" strokeWidth="1.5" strokeLinejoin="round" />
          </svg>
        }
      />
      <StatCard
        label={t('lowStockAlerts')}
        value={formatNumber(stats.lowStockAlerts ?? 0)}
        iconBg="bg-red-50"
        icon={<AlertTriangleIcon size={22} className="text-red-500" />}
      />
      <StatCard
        label={t('totalValue')}
        value={formatCurrency(stats.totalValue ?? stats.averagePrice)}
        iconBg="bg-sky-50"
        icon={
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M15.733 14.533c-3.026-.786-4-1.6-4-2.866 0-1.454 1.347-2.467 3.6-2.467 1.894 0 2.84.72 3.187 1.867.16.533.6.933 1.16.933h.4c.88 0 1.507-.867 1.2-1.693-.56-1.574-1.867-2.88-3.947-3.38V6c0-1.107-.893-2-2-2s-2 .893-2 2v.88c-2.587.56-4.667 2.24-4.667 4.813 0 3.08 2.547 4.614 6.267 5.507 3.333.8 4 1.973 4 3.213 0 .92-.653 2.4-3.6 2.4-2.2 0-3.333-.787-3.773-1.907-.2-.52-.653-.893-1.2-.893h-.373c-.893 0-1.52.907-1.187 1.733.76 1.854 2.533 2.947 4.533 3.374V26c0 1.107.893 2 2 2s2-.893 2-2v-.867c2.6-.493 4.667-2 4.667-4.733 0-3.787-3.24-5.08-6.267-5.867z" fill="#0288D1" />
          </svg>
        }
      />
    </div>
  );
}
